import gql from 'graphql-tag';

/**
 * Fetches the full report catalog for the dashboard view.
 * Returns groups with their report definitions (title, description, icon).
 */
export const REPORT_CATALOG_QUERY = gql`
	query ReportCatalog {
		reportCatalog {
			reportGroupId
			key
			title
			icon
			sortOrder
			reports {
				reportDefinitionId
				key
				title
				description
				sortOrder
			}
		}
	}
`;

/**
 * Fetches a single report definition with all metadata needed for the report viewer:
 * columns, filters, groupings, default sorts, export formats, and the GraphQL query template.
 */
export const REPORT_DEFINITION_QUERY = gql`
	query ReportDefinition($reportKey: String!) {
		reportDefinition(reportKey: $reportKey) {
			reportDefinitionId
			reportGroupId
			key
			title
			description
			graphQlQuery
			graphQlVariablesJson
			resultArrayPath
			sortOrder
			isAvailableToAllFacilities
			requiredPermission
			columns {
				reportColumnId
				key
				label
				dataType
				graphQlFieldPath
				visibleByDefault
				sortable
				filterable
				groupable
				format
				aggregationType
				alignment
				width
				conditionalFormatJson
				sortOrder
			}
			filters {
				reportFilterId
				key
				label
				type
				required
				defaultValue
				operators
				options {
					value
					label
				}
				dependsOnFilterKey
				optionsSourceJson
				sortOrder
			}
			groupings {
				reportGroupingId
				key
				label
				columnKeys
				sortOrder
			}
			defaultSorts {
				reportSortId
				columnKey
				direction
				sortOrder
			}
			exportFormats
		}
	}
`;

// ── Type definitions for query responses ──────────────────────────────────

export interface ReportCatalogGroup {
	reportGroupId: string;
	key: string;
	title: string;
	icon: string | null;
	sortOrder: number;
	reports: ReportCatalogReport[];
}

export interface ReportCatalogReport {
	reportDefinitionId: string;
	key: string;
	title: string;
	description: string;
	sortOrder: number;
}

export interface ReportCatalogQueryResponse {
	reportCatalog: ReportCatalogGroup[];
}

export type ColumnAggregationType = 'SUM' | 'AVG' | 'COUNT' | 'MIN' | 'MAX';
export type ColumnAlignment = 'LEFT' | 'CENTER' | 'RIGHT';

export interface ReportColumn {
	reportColumnId: string;
	key: string;
	label: string;
	dataType: string;
	graphQlFieldPath: string;
	visibleByDefault: boolean;
	sortable: boolean;
	filterable: boolean;
	groupable: boolean;
	format: string | null;
	aggregationType: ColumnAggregationType | null;
	alignment: ColumnAlignment;
	width: string | null;
	conditionalFormatJson: string | null;
	sortOrder: number;
}

export interface ReportFilterOption {
	value: string;
	label: string;
}

export interface ReportFilter {
	reportFilterId: string;
	key: string;
	label: string;
	type: string;
	required: boolean;
	defaultValue: string | null;
	operators: string[];
	options: ReportFilterOption[];
	dependsOnFilterKey: string | null;
	optionsSourceJson: string | null;
	sortOrder: number;
}

export interface ReportGrouping {
	reportGroupingId: string;
	key: string;
	label: string;
	columnKeys: string[];
	sortOrder: number;
}

export interface ReportSort {
	reportSortId: string;
	columnKey: string;
	direction: string;
	sortOrder: number;
}

export interface ReportDefinition {
	reportDefinitionId: string;
	reportGroupId: string;
	key: string;
	title: string;
	description: string;
	graphQlQuery: string | null;
	graphQlVariablesJson: string | null;
	resultArrayPath: string;
	sortOrder: number;
	isAvailableToAllFacilities: boolean;
	requiredPermission: string | null;
	columns: ReportColumn[];
	filters: ReportFilter[];
	groupings: ReportGrouping[];
	defaultSorts: ReportSort[];
	exportFormats: string[];
}

export interface ReportDefinitionQueryResponse {
	reportDefinition: ReportDefinition | null;
}
